import { useState, useRef, useEffect } from 'react';
import { ErrorMessage, useFormikContext } from 'formik';
import { Icon } from '@iconify/react';
import { Label } from '@/components/shadcn/label';
import { Button } from '@/components/shadcn/button';
import { cn } from '@/lib/utils';

interface FileUploadFieldProps {
  name: string;
  label?: string;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  minFiles?: number;
  className?: string;
  required?: boolean;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

const FileUploadField = ({
  name,
  label,
  accept = 'image/*',
  multiple = false,
  maxFiles = 5,
  minFiles,
  className,
  required,
}: FileUploadFieldProps) => {
  const [dragOver, setDragOver] = useState(false);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setFieldValue, setFieldError, values } = useFormikContext<any>();

  const handleFileChange = (files: FileList | null) => {
    if (!files) return;
    const fileArray = Array.from(files);
    const acceptedFiles = fileArray.filter((f) => f.size <= MAX_FILE_SIZE);
    const rejectedFiles = fileArray.filter((f) => f.size > MAX_FILE_SIZE);

    if (rejectedFiles.length > 0) {
      setFieldError(name, `File lebih dari 2 MB: ${rejectedFiles.map((f) => f.name).join(', ')}`);
    } else {
      setFieldError(name, '');
    }

    const limitedFiles = multiple
      ? acceptedFiles.slice(0, maxFiles)
      : acceptedFiles.length
        ? [acceptedFiles[0]]
        : [];
    const newPreviews = limitedFiles.map((f) => URL.createObjectURL(f));
    previews.forEach((url) => URL.revokeObjectURL(url));
    setPreviews(newPreviews);

    if (limitedFiles.length === 0) {
      setFieldValue(name, multiple ? [] : null);
    } else {
      setFieldValue(name, multiple ? limitedFiles : limitedFiles[0]);
    }
  };

  const removeFile = (index: number) => {
    const newPreviews = previews.filter((_, i) => i !== index);
    setPreviews(newPreviews);
    if (multiple) {
      const currentFiles = (values[name] as File[]) || [];
      setFieldValue(
        name,
        currentFiles.filter((_, i) => i !== index)
      );
    } else {
      setFieldValue(name, null);
    }
    URL.revokeObjectURL(previews[index]);
  };

  useEffect(() => {
    const current = values[name];
    if (!current || (Array.isArray(current) && current.length === 0)) {
      previews.forEach((url) => URL.revokeObjectURL(url));
      setPreviews([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } else if (current && previews.length === 0) {
      const fileArray: File[] = Array.isArray(current) ? current : [current];
      setPreviews(fileArray.map((f) => URL.createObjectURL(f)));
    }
  }, [values[name]]);

  useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), []);

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <Label className="mb-2">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      <div
        className={cn(
          'border-2 border-dashed rounded-md p-4 text-center transition-colors border-input bg-muted/30',
          dragOver && 'border-primary bg-primary/10'
        )}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFileChange(e.dataTransfer.files);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragOver(false);
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFileChange(e.target.files)}
          className="hidden"
        />
        <Button
          type="button"
          variant="default"
          onClick={() => fileInputRef.current?.click()}
          className="gap-2"
        >
          <Icon icon="mdi:upload" className="w-4 h-4" />
          Pilih File
        </Button>
        <p className="text-sm text-muted-foreground mt-2">
          {multiple
            ? `*Upload ${minFiles ? `min. ${minFiles} dan ` : ''}maks. ${maxFiles} gambar, maks. 2 MB`
            : '*Upload 1 gambar, maks. 2 MB'}
        </p>
      </div>
      {previews.length > 0 && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
          {previews.map((preview, index) => (
            <div key={index} className="relative group">
              <img
                src={preview}
                alt={`Preview ${index + 1}`}
                className="w-full h-24 object-cover rounded-md border border-input"
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => removeFile(index)}
                aria-label={`Hapus preview ${index + 1}`}
              >
                <Icon icon="mdi:close" className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <ErrorMessage name={name} component="div" className="text-destructive text-xs mt-1" />
    </div>
  );
};

export default FileUploadField;
